import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';
import { qk } from '../../lib/queryClient';
// Built-ins live in @itsm/shared: the SERVER resolves them too, for dashboard
// widgets that name one. Re-exported so existing imports keep working.
import { BUILTIN_VIEWS } from '@itsm/shared';

export { BUILTIN_VIEWS };

/** Saved views — Step 17. Built-ins ship as code and are visually identical to
 *  user views; a stored view is a QUERY STRING that SEEDS the URL (explicit
 *  params in a shared link always win). */
export interface SavedView {
  id: string;
  name: string;
  scope: 'private' | 'group' | 'org';
  query: string;
  ownerId?: string;
  /** Step 42 — starred views are the rail's short list; "All views" is a
   *  directory once a tenant has thirty-five of them. */
  isStarred?: number | boolean;
  pageSize?: number | null;
  columns?: string[] | null;
  /** The LEGACY single group, kept because views written before the join
   *  table exist and must not lose their sharing. */
  groupId?: string | null;
  /**
   * Step 42 — the groups a view is shared with.
   *
   * The server has sent this since the join table landed; the type never
   * learned it, so every consumer was casting `(v as { groupIds?: string[] })`
   * to read a field that was always there. One of those casts per new caller
   * is how a field quietly becomes optional-in-practice.
   */
  groupIds?: string[];
}

/**
 * The built-ins, stored as CONDITIONS rather than chip params — Step 42.
 *
 * Kai: *"The conditions for the seeded views don't show."* They could not:
 * these carried `status=open&assignee=me`, which still drove the query after
 * the chips were removed but had nothing left to render it. A filter you
 * cannot see is worse than no filter, because you cannot tell why work is
 * missing.
 *
 * `@me` and `@myGroups` are resolved by the SERVER against whoever is
 * reading, so these follow the reader rather than whoever wrote them.
 */
/**
 * Keys that are CHROME, not filter — excluded from "is this view modified".
 *
 * `fopen` is the one that mattered: merely opening the filter panel changed
 * the URL, so the view showed a "modified" badge and offered Save / Reset for
 * a change nobody had made. Reset then restored an identical filter, which is
 * why Kai reported it as doing nothing — it was working perfectly on a
 * difference that did not exist.
 *
 * `limit` is the same class: paging deeper is not a different view.
 */
export const VIEW_TRANSIENT_KEYS = ['view', 'peek', 'cursor', 'fopen', 'limit'];

/** The current filter state as a canonical string — sorted keys, transient
 *  keys removed — so "modified vs the view" is a string comparison that can't
 *  be fooled by parameter order. */
export function canonicalQuery(params: URLSearchParams): string {
  const entries = [...params.entries()]
    .filter(([k]) => !VIEW_TRANSIENT_KEYS.includes(k))
    .sort(([a], [b]) => a.localeCompare(b));
  return new URLSearchParams(entries).toString();
}

export function canonicalOf(query: string): string {
  return canonicalQuery(new URLSearchParams(query));
}

/** Rows a view shows when it doesn't say. Named because three places need the
 *  same number — the queue's initial depth, its "Load more" step, and the
 *  editor's "Default (50)" label — and a view whose label disagrees with its
 *  behaviour is worse than no label. */
export const DEFAULT_PAGE_SIZE = 50;

/**
 * The views a person can open — built-ins first, then their stored ones.
 *
 * Lifted out of `TicketsPage` when the dashboard needed it too: a widget
 * counts a VIEW, so both pages have to agree on what the list of views is,
 * and two copies of the fetch would be two caches that disagree after a
 * rename.
 */
export function useViews(): SavedView[] {
  const { data } = useQuery({
    queryKey: qk.views,
    queryFn: () => apiFetch<{ entries: SavedView[] }>('/api/views'),
    staleTime: 60_000,
  });
  return useMemo(() => [...BUILTIN_VIEWS, ...(data?.entries ?? [])], [data]);
}
